/* eslint-disable @typescript-eslint/no-explicit-any */
import { THEME } from '@common/constants/theme.constant';
import { AntDatePickerWithDayjs } from '@common/general-ui/date-picker/ant-date-picker.component';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { IDateRange } from '@common/interfaces/date-range.interface';
import dayjs, { Dayjs } from 'dayjs';
import { RangeValue } from 'rc-picker/lib/interface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from 'twin.macro';

const { RangePicker: AntRangePicker } = AntDatePickerWithDayjs;

export type IRangePickerChangeHandler = (props: { name: string } & IDateRange) => void;

export interface IRangePickerProps<TName = string> extends IFieldProps<IDateRange, TName> {
    disableDate?: (date: any) => boolean;
    className?: string;
    onChange?: IRangePickerChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    range?: number;
}

export const RangePicker = (props: IRangePickerProps): JSX.Element => {
    const { name, onChange, onBlur, value, range = 0 } = props;
    const [calendarValue, setCalendarValue] = useState<IDateRange>();

    const { className, ...fieldProps } = props;

    const handleChange = useCallback(
        (val: (Dayjs | undefined)[]) => {
            onChange &&
                onChange({
                    name,
                    valueFrom: val && val[0] ? val[0].toDate() : undefined,
                    valueTo: val && val[1] ? val[1].toDate() : undefined,
                });
        },
        [name, onChange],
    );

    const dayjsValue = useMemo(
        () => [value?.valueFrom ? dayjs(value?.valueFrom) : undefined, value?.valueTo ? dayjs(value?.valueTo) : undefined],
        [value?.valueFrom, value?.valueTo],
    );

    const calendarChangeHandler = useCallback((val: RangeValue<Dayjs>) => {
        setCalendarValue({
            valueFrom: val && val[0] ? val[0].toDate() : undefined,
            valueTo: val && val[1] ? val[1].toDate() : undefined,
        });
    }, []);

    const disabledDateWithinRange = useCallback(
        (current: Dayjs): boolean => {
            const tooLate = calendarValue?.valueFrom && current.diff(calendarValue.valueFrom, 'days') > range;
            const tooEarly = calendarValue?.valueTo && dayjs(calendarValue.valueTo).diff(current, 'days') > range;
            return !!tooEarly || !!tooLate;
        },
        [calendarValue, range],
    );

    useEffect(() => {
        return (): void => {
            if (range) {
                setCalendarValue(undefined);
            }
        };
    }, [range]);

    return (
        <Field {...fieldProps} data-testid={`field-${name}`} className={className}>
            <div css={styles.root} tw="relative">
                {props.readOnly && <Readonly />}
                <AntRangePicker
                    disabledDate={range ? disabledDateWithinRange : props.disableDate}
                    inputReadOnly={props.readOnly}
                    tabIndex={props.readOnly ? -1 : 0}
                    name={name}
                    panelRender={(original: JSX.Element): JSX.Element => <div css={styles.panel}>{original}</div>}
                    onChange={handleChange as any}
                    value={dayjsValue as any}
                    onBlur={onBlur}
                    onCalendarChange={range ? calendarChangeHandler : undefined}
                />
            </div>
        </Field>
    );
};

const styles = {
    root: css`
        .ant-picker {
            width: 100%;
        }
    `,
    panel: css`
        .ant-picker-cell-in-range::before,
        .ant-picker-cell-range-start::before,
        .ant-picker-cell-range-end::before {
            background-color: ${THEME.GRAY}!important;
        }
    `,
};
